import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Bug, Lightbulb, MessageSquare, Check, Archive, Inbox, RotateCcw, Search, X,
  ChevronLeft, ChevronRight, MessagesSquare,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';
import { formatDateTime } from '@/lib/dateFormat';
import { SelectField } from '@/components/ui/SelectField';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin Product Feedback inbox — Bug / Feature / General feedback from
// the vendor "Send Feedback" widget, kept SEPARATE from support tickets.
// Layout follows the other list pages: KPI tiles, status tabs, search +
// category filter, merged-column table with more/less, paging.
// ────────────────────────────────────────────────────────────────────────────

interface Feedback {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  name: string | null;
  email: string | null;
  category: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

const CATS: Record<string, { label: string; icon: any; cls: string }> = {
  bug: { label: 'Bug', icon: Bug, cls: 'bg-rose-50 text-rose-600 border-rose-200' },
  feature: { label: 'Feature', icon: Lightbulb, cls: 'bg-amber-50 text-amber-600 border-amber-200' },
  general: { label: 'General', icon: MessageSquare, cls: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
};

const STATUS_STYLE: Record<string, string> = {
  new: 'bg-blue-50 text-blue-700',
  reviewed: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-slate-100 text-slate-500',
};

const TABS = [
  { id: 'new', label: 'New' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'archived', label: 'Archived' },
  { id: 'all', label: 'All' },
] as const;

const MSG_LIMIT = 140;

export function SuperAdminFeedbackPage() {
  const [tab, setTab] = useState<string>('new');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [items, setItems] = useState<Feedback[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [pg, setPg] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [tab, category, debounced, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { status: tab, page, limit: pageSize };
      if (category) params.category = category;
      if (debounced) params.search = debounced;
      const { data } = await saApi.get('/super-admin/product-feedback', { params });
      setItems(data?.items ?? []);
      setCounts(data?.counts ?? {});
      setCategoryCounts(data?.categoryCounts ?? {});
      setPg(data?.pagination ?? { page, limit: pageSize, total: (data?.items ?? []).length, totalPages: 1 });
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to load feedback'));
    } finally {
      setLoading(false);
    }
  }, [tab, page, pageSize, category, debounced]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (f: Feedback, status: string) => {
    setBusyId(f.id);
    try {
      await saApi.patch(`/super-admin/product-feedback/${f.id}`, { status });
      toast('success', status === 'new' ? 'Moved back to New' : `Marked ${status}`);
      await load();
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to update'));
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (id: string) => setExpanded(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const totalAll = (counts.new ?? 0) + (counts.reviewed ?? 0) + (counts.archived ?? 0);
  const kpis = [
    { label: 'Total feedback', value: totalAll, sub: `${counts.reviewed ?? 0} reviewed · ${counts.archived ?? 0} archived`, icon: <MessagesSquare className="h-4 w-4" />, tone: 'bg-indigo-50 text-[#6366F1]' },
    { label: 'New', value: counts.new ?? 0, sub: 'Waiting for review', icon: <Inbox className="h-4 w-4" />, tone: 'bg-blue-50 text-blue-600' },
    { label: 'Bugs', value: categoryCounts.bug ?? 0, sub: 'Reported problems', icon: <Bug className="h-4 w-4" />, tone: 'bg-rose-50 text-rose-600' },
    { label: 'Feature requests', value: categoryCounts.feature ?? 0, sub: `${categoryCounts.general ?? 0} general`, icon: <Lightbulb className="h-4 w-4" />, tone: 'bg-amber-50 text-amber-600' },
  ];

  const hasFilters = !!(debounced || category);
  const from = pg.total === 0 ? 0 : (pg.page - 1) * pg.limit + 1;
  const to = Math.min(pg.page * pg.limit, pg.total);
  const start = Math.max(1, Math.min(pg.page - 2, pg.totalPages - 4));
  const pageNums = Array.from({ length: Math.min(5, pg.totalPages) }, (_, i) => start + i);
  const btn = 'h-8 min-w-8 px-2 rounded-lg text-xs border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-600';

  const renderMessage = (f: Feedback) => {
    const long = f.message.length > MSG_LIMIT;
    const open = expanded.has(f.id);
    return (
      <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
        {long && !open ? `${f.message.slice(0, MSG_LIMIT).trimEnd()}…` : f.message}
        {long && (
          <button type="button" onClick={() => toggle(f.id)} className="ml-1 text-xs font-medium text-[#6366F1] hover:underline">
            {open ? 'less' : 'more'}
          </button>
        )}
      </p>
    );
  };

  const actions = (f: Feedback) => (
    <div className="flex flex-wrap justify-end gap-1.5">
      {busyId === f.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
        <>
          {f.status !== 'reviewed' && (
            <button onClick={() => setStatus(f, 'reviewed')} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 text-xs font-medium"><Check className="h-3 w-3" /> Reviewed</button>
          )}
          {f.status !== 'archived' && (
            <button onClick={() => setStatus(f, 'archived')} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-500"><Archive className="h-3 w-3" /> Archive</button>
          )}
          {f.status !== 'new' && (
            <button onClick={() => setStatus(f, 'new')} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-500"><RotateCcw className="h-3 w-3" /> Reopen</button>
          )}
        </>
      )}
    </div>
  );

  const fromLine = (f: Feedback) => (
    <>
      <p className="text-slate-800">{f.name || 'Someone'}</p>
      {f.email && <p className="text-xs text-slate-500 mt-0.5 break-all">{f.email}</p>}
      {f.tenantName && (
        f.tenantId ? (
          <Link to={`/super-admin/organizations?open=${f.tenantId}`} className="text-xs text-[#6366F1] hover:underline mt-0.5 inline-block">{f.tenantName}</Link>
        ) : (
          <p className="text-xs text-slate-500 mt-0.5">{f.tenantName}</p>
        )
      )}
    </>
  );

  return (
    <SuperAdminLayout title="Feedback">
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Feedback</h1>
          <p className="text-sm text-slate-500 mt-0.5">Bugs, feature requests and general feedback sent by vendors from the app.</p>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {kpis.map(k => (
            <div key={k.label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="flex items-start justify-between">
                <p className="text-sm text-slate-500">{k.label}</p>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${k.tone}`}>{k.icon}</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900">{k.value}</p>
              <p className="mt-1 text-xs text-slate-500">{k.sub}</p>
            </div>
          ))}
        </div>

        {/* Status tabs + search + category */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {TABS.map(t => {
              const active = tab === t.id;
              const n = t.id === 'all' ? totalAll : counts[t.id] ?? 0;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-8 text-sm ${active ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  {t.label}
                  {n > 0 && <span className={`text-[11px] px-1.5 rounded-full ${active ? 'bg-white/25' : 'bg-slate-100 text-slate-500'}`}>{n}</span>}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search subject, message, sender or organization..."
              className="h-10 w-80 max-w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            />
          </div>
          <SelectField
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
          >
            <option value="">All categories</option>
            <option value="bug">Bug</option>
            <option value="feature">Feature</option>
            <option value="general">General</option>
          </SelectField>
          {hasFilters && (
            <button onClick={() => { setSearch(''); setCategory(''); }} className="inline-flex h-10 items-center gap-1 px-2 text-sm text-slate-500 hover:text-slate-800">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Inbox className="h-8 w-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">{hasFilters ? 'No feedback matches these filters' : `No ${tab === 'all' ? '' : tab} feedback`}</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {items.map((f, idx) => {
                  const cat = CATS[f.category] ?? CATS.general;
                  const Icon = cat.icon;
                  return (
                    <div key={f.id} className="px-4 py-3 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-400">{from + idx}.</span>
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cat.cls}`}><Icon className="h-3 w-3" /> {cat.label}</span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] capitalize ${STATUS_STYLE[f.status] ?? STATUS_STYLE.archived}`}>{f.status}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-900">{f.subject}</p>
                      {renderMessage(f)}
                      <div className="text-xs text-slate-500">{fromLine(f)}<p className="mt-0.5">{formatDateTime(f.createdAt)}</p></div>
                      {actions(f)}
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#6366F1]">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-white">
                      <th className="px-4 py-3 w-12">S.No</th>
                      <th className="px-4 py-3">Feedback</th>
                      <th className="px-4 py-3">From</th>
                      <th className="px-4 py-3">Message</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((f, idx) => {
                      const cat = CATS[f.category] ?? CATS.general;
                      const Icon = cat.icon;
                      return (
                        <tr key={f.id} className="align-top hover:bg-slate-50/70">
                          <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{from + idx}</td>
                          <td className="px-4 py-3 max-w-[260px]">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cat.cls}`}><Icon className="h-3 w-3" /> {cat.label}</span>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] capitalize ${STATUS_STYLE[f.status] ?? STATUS_STYLE.archived}`}>{f.status}</span>
                            </div>
                            <p className="mt-1 font-medium text-slate-900 break-words">{f.subject}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{formatDateTime(f.createdAt)}</p>
                          </td>
                          <td className="px-4 py-3 min-w-[180px]">{fromLine(f)}</td>
                          <td className="px-4 py-3 max-w-[420px]">{renderMessage(f)}</td>
                          <td className="px-4 py-3">{actions(f)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paging */}
          {pg.total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span>Showing {from}–{to} of {pg.total}</span>
                <label className="flex items-center gap-1.5">
                  Rows per page
                  <SelectField
                    value={pageSize}
                    onChange={e => setPageSize(Number(e.target.value))}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                  >
                    {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </SelectField>
                </label>
              </div>
              <div className="flex items-center gap-1">
                <span className="mr-2 text-xs text-slate-500">Page {pg.page} of {pg.totalPages}</span>
                <button className={btn} disabled={pg.page <= 1} onClick={() => setPage(1)}>First</button>
                <button className={btn} disabled={pg.page <= 1} onClick={() => setPage(pg.page - 1)} aria-label="Previous page"><ChevronLeft className="h-4 w-4 mx-auto" /></button>
                {pageNums.map(n => (
                  <button key={n} onClick={() => setPage(n)} className={n === pg.page ? 'h-8 min-w-8 px-2 rounded-lg text-xs bg-[#6366F1] text-white' : btn}>{n}</button>
                ))}
                <button className={btn} disabled={pg.page >= pg.totalPages} onClick={() => setPage(pg.page + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4 mx-auto" /></button>
                <button className={btn} disabled={pg.page >= pg.totalPages} onClick={() => setPage(pg.totalPages)}>Last</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
